// src/components/shared/DocumentViewerPanel.tsx
import React, { useEffect, useState } from "react";
import { Loader2, FileX } from "lucide-react";
import { useTranslations } from "use-intl";
import { InvoiceApiClient } from "@/clients/invoiceApiClient";

interface DocumentViewerPanelProps {
  filePath: string;
  fileName?: string; // Used for file type detection — falls back to filePath
  resolveUrl?: (filePath: string) => Promise<string | null>;
}

const DocumentViewerPanel: React.FC<DocumentViewerPanelProps> = ({
  filePath,
  fileName,
  resolveUrl = InvoiceApiClient.getInvoiceFileUrl.bind(InvoiceApiClient),
}) => {
  const t = useTranslations("consultantReceivables.documentViewer");
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    setUrl(null);

    resolveUrl(filePath)
      .then((signedUrl) => {
        if (cancelled) return;
        if (!signedUrl) {
          setError(true);
        } else {
          setUrl(signedUrl);
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [filePath]);

  const isPdf = (fileName ?? filePath).toLowerCase().endsWith(".pdf");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error || !url) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground">
        <FileX className="h-8 w-8" />
        <p className="text-sm">{t("unavailable")}</p>
      </div>
    );
  }

  if (isPdf) {
    return (
      <div className="h-full flex flex-col">
        <iframe
          src={url}
          className="flex-1 w-full border-0"
          title={t("preview")}
          onError={() => setError(true)}
        />
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-center text-blue-600 hover:underline py-1 border-t"
        >
          {t("openInNewTab")}
        </a>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 flex items-center justify-center overflow-auto p-2">
        <img
          src={url}
          alt={t("preview")}
          className="max-w-full max-h-full object-contain"
          onError={() => setError(true)}
        />
      </div>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs text-center text-blue-600 hover:underline py-1 border-t"
      >
        {t("openInNewTab")}
      </a>
    </div>
  );
};

export default DocumentViewerPanel;